// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useMagicLinkLogin } from '@/authentication/hooks/use-magic-link-login';
import { useRequestMagicLink } from '@/authentication/hooks/use-request-magic-link';
import { MailOutlined } from '@/constants/hardfin-standard-icons';
import { followLoginRedirect } from '@/utilities/login-redirect';
import { Alert, Button, Collapse, Stack, TextField } from '@mui/material';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { FormEvent, useEffect, useState } from 'react';

const INVALID_LINK_MESSAGE =
  'This sign-in link is invalid or has expired. Request a new one.';
const UNKNOWN_ERROR_MESSAGE =
  'Unknown error, please contact support for additional help';

interface MagicLinkFormProps {
  linkID?: string;
  redirectURL?: string;
}

/** Requests an emailed sign-in link, and signs in with its id when the page is opened from one. */
export function MagicLinkForm(props: MagicLinkFormProps) {
  const navigate = useNavigate();
  const router = useRouter();
  const [expanded, setExpanded] = useState(false);
  const [email, setEmail] = useState('');
  const requestMagicLink = useRequestMagicLink();
  const magicLinkLogin = useMagicLinkLogin();
  const { mutate: login } = magicLinkLogin;

  useEffect(() => {
    if (props.linkID === undefined) {
      return;
    }

    const linkID = props.linkID;
    // A link signs in once, so StrictMode's discarded first mount must not spend it.
    const loginTimer = setTimeout(() => {
      login(linkID, {
        onSuccess: () =>
          followLoginRedirect(props.redirectURL, {
            toHome: () => void navigate({ to: '/assets' }),
            toRoute: (path) => {
              router.history.push(path);
            },
          }),
      });
    });
    return () => clearTimeout(loginTimer);
  }, [props.linkID, props.redirectURL, login, navigate, router]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    requestMagicLink.mutate(
      { email, redirectURL: props.redirectURL },
      { onSuccess: () => setEmail('') }
    );
  };

  return (
    <Stack spacing={2} width="100%">
      {magicLinkLogin.isError && (
        <Alert severity="error">{INVALID_LINK_MESSAGE}</Alert>
      )}
      {requestMagicLink.isSuccess && (
        <Alert severity="success">
          A sign in link was sent to {requestMagicLink.variables.email}
        </Alert>
      )}
      {requestMagicLink.isError && (
        <Alert severity="error">{UNKNOWN_ERROR_MESSAGE}</Alert>
      )}
      <Button
        fullWidth
        variant="outlined"
        startIcon={<MailOutlined />}
        onClick={() => setExpanded(!expanded)}
      >
        Email me a link
      </Button>
      <Collapse in={expanded} unmountOnExit>
        <Stack
          component="form"
          direction="row"
          spacing={1}
          onSubmit={handleSubmit}
        >
          <TextField
            label="Email"
            type="email"
            size="small"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            required
            disabled={requestMagicLink.isPending || magicLinkLogin.isPending}
          />
          <Button
            type="submit"
            variant="contained"
            size="small"
            disabled={requestMagicLink.isPending || magicLinkLogin.isPending}
          >
            Send
          </Button>
        </Stack>
      </Collapse>
    </Stack>
  );
}
